import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Toolbar from "../../controls/Toolbar";
import { Button } from "react-bootstrap";
import { useHasPermission } from "../../../common/Hooks";
import Utils from "../../../utils/utils";
import { useParams } from "react-router-dom";
import ACTION_TYPES from "../../../config/actionTypes.js";
import moment from "moment";
import Deployment from "../../../services/deployment.js";
import PubSub from "../../../services/PubSub.js";
import UploadArea from "../../controls/UploadArea.jsx";
import { Form, Formik } from 'formik';
import TextBox from "../../controls/form/TextBox.jsx";
import ConfirmDeployModal from "./modals/ConfirmDeployModal.jsx";
import { useFormsConfig } from "./Context.js";
import TextArea from "../../controls/form/TextArea.jsx";
import ListStatus from "./controls/ListStatus.jsx";
import Label from "../../controls/form/Label.jsx";
import GenericTextBox from "../../controls/GenericTextBox.jsx";
import ActionButton from "../../settings/ActionButton.jsx";

const DATE_MOMENT_FORMAT = "DD-MMM-yyyy HH:mm";

const DeploymentSettings = ({
    deployment,
    deploymentUrl
}) => {
    const { id: deploymentId } = useParams();
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "deployment"
    });
    const [translate] = useTranslation();

    const { defaultBackgroundColor } = useFormsConfig();
    const [actionInProgress, setActionInProgress] = useState(ACTION_TYPES.AVAILABLE);
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showConfirmDeployModal, setShowConfirmDeployModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [welcomeMessage, setWelcomeMessage] = useState('');

    const allowedUpdate = useMemo(() => {
        return !isLoading && permissions.update && actionInProgress === ACTION_TYPES.AVAILABLE;
    }, [isLoading, permissions.update, actionInProgress]);

    const handleSubmit = useCallback(async (values) => {
        setActionInProgress(ACTION_TYPES.UPLOAD_RESOURCES);
        const response = await Deployment.updateDeployment(deploymentId, {
            installAsAppName: values.installAsAppName,
            backgroundColor: values.backgroundColor,
            welcomeMessage: values.welcomeMessage
        });

        if (response && !response.error) {
            PubSub.publish("show_info", "OTM_SAVE_CHANGES_SUCCESS", "OTM_SUCCESS");
            PubSub.publish("refresh-forms-preferences");
        }

        setActionInProgress(ACTION_TYPES.AVAILABLE);
        setShowSaveButton(false);
    }, [deploymentId]);

    const getWelcomeMessage = useCallback(async (deploymentId) => {
        setIsLoading(true);
        const response = await Deployment.getFormsWelcomeMessage(deploymentId);

        if (response && !response.error) {
            setWelcomeMessage(response.data);
        }

        setIsLoading(false);
    }, []);

    const handleOnChange = useCallback(() => {
        setShowSaveButton(true);
    }, []);

    const checkDeployStatus = useCallback((deploymentId) => {
        Deployment.getDeployment(deploymentId, false)
            .then((response) => {
                const retrievedDeployment = response.data;

                if (retrievedDeployment.actionInProgress !== ACTION_TYPES.DEPLOYMENT_IN_PROGRESS) {
                    PubSub.publish('timer-stop', deploymentId);
                    PubSub.publish('refresh-forms-preferences');
                    setActionInProgress(ACTION_TYPES.AVAILABLE);
                }

                if (retrievedDeployment.otmApp.deployMessages.length <= 0) {
                    return;
                }

                const message = retrievedDeployment.otmApp.deployMessages.findLast((el) => el.displayMessage === true);
                if (message) {
                    PubSub.publish('show_deployment_info', deploymentId, message.message);
                }
            });
    }, []);

    const handleDeploy = useCallback((deploymentId) => {
        Deployment.deploy(deploymentId)
            .then(() => {
                checkDeployStatus(deploymentId);
            });

        PubSub.publish('show_deployment_info', deploymentId, translate('OTM_STARTED_DEPLOYMENT'), translate('OTM_DEPLOYMENT_STATUS'))
        setActionInProgress(ACTION_TYPES.DEPLOYMENT_IN_PROGRESS);

        PubSub.publish('timer-start', deploymentId, checkDeployStatus);
    }, [checkDeployStatus, translate]);

    const uploadLogo = useCallback(async (id, data) => {
        setActionInProgress(ACTION_TYPES.UPLOAD_LOGO);
        const response = await Deployment.uploadLogo(id, data);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    const uploadFavicon = useCallback(async (id, data) => {
        setActionInProgress(ACTION_TYPES.UPLOAD_FAVICON);
        const response = await Deployment.uploadFavicon(id, data);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    const handleDeleteFavicon = useCallback(async (deploymentId) => {
        setActionInProgress(ACTION_TYPES.DELETE_FAVICON);
        const response = await Deployment.deleteFavicon(deploymentId);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    const handleDeleteLogo = useCallback(async (deploymentId) => {
        setActionInProgress(ACTION_TYPES.DELETE_LOGO);
        const response = await Deployment.deleteLogo(deploymentId);
        setActionInProgress(ACTION_TYPES.AVAILABLE);
        return response;
    }, []);

    useEffect(() => {
        if (!welcomeMessage && deploymentId) {
            getWelcomeMessage(deploymentId);
        }
    }, [welcomeMessage, deploymentId, getWelcomeMessage])

    useEffect(() => {
        if (deployment.actionInProgress === ACTION_TYPES.DEPLOYMENT_IN_PROGRESS) {
            PubSub.publish('timer-start', deploymentId, checkDeployStatus);
            setActionInProgress(ACTION_TYPES.DEPLOYMENT_IN_PROGRESS);
        }
    }, [deployment, checkDeployStatus, deploymentId]);

    const renderHeader = useMemo(() => <div className='card-header border-bottom'>
        <Toolbar
            heading={translate('OTM_APP_AND_PORTAL_CONFIGURATION')}
            buttons={[
                {
                    text: translate("OTM_UPDATE_APP_AND_PORTAL"),
                    variant: "primary",
                    className: "py-1 px-2",
                    hidden: showSaveButton,
                    disabled: !allowedUpdate,
                    onClick: () => {
                        setShowConfirmDeployModal(true);
                    }
                },
                {
                    text: translate("OTM_SAVE"),
                    variant: "primary",
                    className: "py-1 px-2",
                    hidden: !showSaveButton,
                    disabled: !allowedUpdate,
                    type: "submit",
                },
            ]}
        />
    </div>, [allowedUpdate, showSaveButton, translate]);

    const renderDeploymentSettings = useMemo(() => {
        if (isLoading) {
            return <div className="col-12 mt-3">
                <ListStatus loading={isLoading} error={false} empty={true} />
            </div>
        }

        return <>
            <div className="col-12 col-md-6 mt-3">
                <TextBox
                    disabled={!allowedUpdate}
                    label={translate("OTM_INSTALL_AS_APP_NAME")}
                    tooltip={translate("OTM_INSTALL_AS_APP_NAME_TOOLTIP")}
                    buttonTooltip={true}
                    name="installAsAppName"
                    type="text"
                />
            </div>
            <div className="col-12 col-md-6 mt-3">
                <TextBox
                    style={{ minHeight: '42px' }}
                    type="color"
                    disabled={!allowedUpdate}
                    label={translate("OTM_BACKGROUND_COLOR")}
                    name="backgroundColor"
                />
            </div>
            <div className="col-12 mt-3">
                <TextArea
                    disabled={!allowedUpdate}
                    label={translate("OTM_WELCOME_MESSAGE")}
                    tooltip={translate("OTM_WELCOME_MESSAGE_TOOLTIP")}
                    buttonTooltip={true}
                    name="welcomeMessage"
                    type="text"
                />
            </div>
            <div className="col-12"></div>
            {
                deployment.productVersion?.enableOverrideLogoIcon &&
                <>
                    <div className="col-12 col-sm-6 mt-3 align-self-start">
                        <Label
                            label={translate("OTM_UPLOAD_LOGO")}
                            tooltip={translate("OTM_UPLOAD_LOGO_TOOLTIP")}
                            buttonTooltip={true}
                        />
                        <UploadArea
                            dropText={"OTM_DROP_LOGO"}
                            dragText={"OTM_DRAG_LOGO"}
                            disabled={!allowedUpdate}
                            parentId={deployment._id}
                            uploadCallback={uploadLogo}
                        />
                        <div className='mt-2'>
                            <Button
                                disabled={!allowedUpdate}
                                variant='primary'
                                onClick={() => handleDeleteLogo(deployment._id)}>
                                {translate('OTM_REVERT_TO_DEFAULT')}
                            </Button>
                        </div>
                    </div>
                    <div className="col-12 col-sm-6 mt-3 align-self-start">
                        <Label
                            label={translate("OTM_UPLOAD_FAVICON")}
                            tooltip={translate("OTM_UPLOAD_FAVICON_TOOLTIP")}
                            buttonTooltip={true}
                        />
                        <UploadArea
                            dropText={"OTM_DROP_FAVICON"}
                            dragText={"OTM_DRAG_FAVICON"}
                            disabled={!allowedUpdate}
                            parentId={deployment._id}
                            uploadCallback={uploadFavicon}
                        />
                        <div className='mt-2'>
                            <Button
                                disabled={!allowedUpdate}
                                variant='primary'
                                onClick={() => handleDeleteFavicon(deployment._id)}>
                                {translate('OTM_REVERT_TO_DEFAULT')}
                            </Button>
                        </div>
                    </div>
                </>
            }
        </>
    }, [allowedUpdate, isLoading, deployment, handleDeleteFavicon, handleDeleteLogo, uploadFavicon, uploadLogo, translate]);

    return <div className="h-100">
        {showConfirmDeployModal && <ConfirmDeployModal
            onConfirm={() => {
                handleDeploy(deployment._id);
                setShowConfirmDeployModal(false);
            }}
            onClose={() => { setShowConfirmDeployModal(false); }}
        />}
        <Formik initialValues={{
            installAsAppName: deployment.installAsAppName ?? "",
            backgroundColor: deployment.backgroundColor ?? defaultBackgroundColor ?? "",
            welcomeMessage: welcomeMessage ?? ""
        }}
            validateOnMount
            onSubmit={handleSubmit}
            enableReinitialize>
            {(props) => (
                <Form onChange={handleOnChange} className="container-fluid h-100">
                    <div className='card h-100'>
                        {renderHeader}
                        <div className='card-body h-100 overflow-auto'>
                            <div className='row align-items-center mt-n3'>
                                <div className="col-12 col-lg-6 mt-3">
                                    <label className='h6 d-block'>{translate('OTM_DEPLOYMENT_URL')}</label>
                                    <a className='h6 text-primary' rel='noreferrer' target='_blank' href={deploymentUrl}>
                                        {deploymentUrl}
                                        {deployment.otmApp?.cloudfrontId ? <></> : <label className='ms-1' style={{ color: 'red' }}> {translate('OTM_NOT_DEPLOYED')}</label>}
                                    </a>
                                </div>
                                <div className="col-12 col-lg-6 mt-3">
                                    <Label
                                        controlId={"deploymentId"}
                                        label={translate("OTM_DEPLOYMENT_ID")}
                                    />
                                    <div className="row gx-2 align-items-center">
                                        <div className="col">
                                            <GenericTextBox
                                                defaultValue={deployment._id}
                                                disabled={true}
                                                readOnly={true} />
                                        </div>
                                        <div className="col-auto">
                                            <ActionButton actionType="copy" valueToCopy={deployment._id} />
                                        </div>
                                    </div>
                                </div>

                                <div className='col-12 col-sm-6 mt-3'>
                                    <Label
                                        label={translate('OTM_LAST_DEPLOYMENT_UPDATE')}
                                        tooltip={translate('OTM_LAST_DEPLOYMENT_UPDATE_TOOLTIP')}
                                        buttonTooltip={true}
                                    />
                                    <span className="d-block">
                                        {deployment.otmApp?.lastDeployTime ?
                                            moment(deployment.otmApp.lastDeployTime).format(DATE_MOMENT_FORMAT) :
                                            translate("OTM_NO_TIME_RECORDED")}
                                    </span>
                                </div>
                                <div className='col-12 col-sm-6 mt-3'>
                                    <label className='h6'>{translate("OTM_PRODUCT_VERSION")}</label>
                                    <span className="d-block">{deployment.productVersion?.version}</span>
                                </div>

                                <div className="col-12 mt-3">
                                    <span className="h5">{translate("OTM_DEPLOYMENT_SETTINGS_INFO")}</span>
                                </div>

                                {renderDeploymentSettings}
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    </div>
}

export default DeploymentSettings;